import React, { useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Card,
    Empty,
    Skeleton,
    Space,
    Switch,
    Tabs,
    Tag,
    Typography,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi, liveSettingsKeys } from '../../../services/ai-admin-api';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';
import { PromptEditor } from '../shared/PromptEditor';
import {
    ProviderModelTempForm,
    ProviderModelTempValue,
    ProviderId,
} from '../shared/ProviderModelTempForm';

const { Text, Paragraph } = Typography;

interface HypothesisAIConfigRow {
    id?: number;
    config_key: string;
    system_prompt: string;
    user_prompt_template: string;
    is_active?: boolean | number;
    [k: string]: unknown;
}

interface HypothesisAiPayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string | undefined>;
    configTableRows: {
        hypothesis_ai_config?: HypothesisAIConfigRow[];
    };
}

interface HypothesisKeyInfo {
    configKey: string;
    label: string;
    description: string;
}

// Mirrors the config_keys in hypothesis_ai_config (SYR-90).
const HYPOTHESIS_KEYS: HypothesisKeyInfo[] = [
    {
        configKey: 'research_synthesis',
        label: 'Research synthesis',
        description:
            'Turns the fetched evidence (website pages + Google listings + review corpus + competitor sites) into the research record held against the subject (admin-only; never written to Brand Knowledge). Returns { publicFootprint, customerSentiment, competitiveLandscape, fieldSuggestions, evidenceNotes }. Must stay strictly evidence-bound; review patterns carry their location scope.',
    },
    {
        configKey: 'competitor_identification',
        label: 'Competitor identification',
        description:
            'Small pre-fetch call (Phase 6B): names up to HYPOTHESIS_MAX_COMPETITORS competitors (customer-supplied key competitors preferred, else inferred from the site) before their sites are fetched. Returns { competitors: [{ name, websiteUrl }] }.',
    },
    {
        configKey: 'hypothesis',
        label: 'The Hypothesis',
        description:
            'Forms the inferred-constraint hypothesis from the research (Phase 6C: element scorecard). Returns { constraintElement, darkHorseElement, headline, claim, elementReadings[], closingQuestion } — one reading per element in the HYPOTHESIS_ELEMENTS set, exactly one LIKELY_CONSTRAINT. Element names must come from the element set in the input.',
    },
    {
        configKey: 'reveal',
        label: 'Reveal narrative',
        description:
            'Writes the confirm/overturn reveal narrative once the response threshold resolves the hypothesis (Phase 4). The verdict itself is deterministic code — this prompt only narrates it. Returns { headline, body }.',
    },
];

const PLACEHOLDERS = (
    <span>
        <code>{'{input_text}'}</code> — the compiled research evidence (website
        page extracts, Google listing aggregate, review texts) built
        deterministically before the call
    </span>
);

function parseModelList(raw: string | undefined): Array<{ id: string; label?: string }> {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry: unknown) => {
                if (typeof entry === 'string') return { id: entry };
                if (entry && typeof entry === 'object') {
                    const e = entry as { id?: string; label?: string };
                    if (e.id) return { id: e.id, label: e.label };
                }
                return null;
            })
            .filter((m): m is { id: string; label?: string } => m !== null);
    } catch {
        return [];
    }
}

function guessProvider(modelId: string | undefined | null): ProviderId {
    if (!modelId) return 'anthropic';
    if (modelId.startsWith('claude') || modelId.startsWith('anthropic')) return 'anthropic';
    return 'openai';
}

function isActiveBool(v: boolean | number | undefined): boolean {
    return v === true || v === 1;
}

/**
 * Configure tab body for The Hypothesis AI (SYR-90).
 *
 * Enabled toggle + provider config + per-key prompts (research_synthesis ·
 * hypothesis), plus the research-job table (visibility, retry, and the
 * white-glove per-brand trigger). Modelled on The Briefing AI page.
 */
export const TheHypothesisAiConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<HypothesisAiPayload>();
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [anthropicModels, setAnthropicModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [openaiModels, setOpenaiModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [googleModels, setGoogleModels] = useState<Array<{ id: string; label?: string }>>([]);

    useEffect(() => {
        if (!jwt) return;
        aiAdminApi
            .getSubsystem(jwt, 'models_providers')
            .then((mp) => {
                const keys = liveSettingsKeys(mp);
                setAnthropicModels(
                    parseModelList(keys.AVAILABLE_ANTHROPIC_MODELS),
                );
                setOpenaiModels(
                    parseModelList(keys.AVAILABLE_OPENAI_MODELS),
                );
                setGoogleModels(
                    parseModelList(keys.AVAILABLE_GOOGLE_MODELS),
                );
            })
            .catch(() => {
                // Non-fatal — admin can still type a model id manually.
            });
    }, [jwt]);

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 12 }} />;
    }

    const settings = editor.payload.systemSettingsKeys ?? {};
    const rows = editor.payload.configTableRows?.hypothesis_ai_config ?? [];

    const setSetting = (key: string, value: string) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));
    };

    const setManySettings = (patch: Record<string, string>) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, ...patch },
        }));
    };

    const upsertRow = (configKey: string, patch: Partial<HypothesisAIConfigRow>) => {
        editor.setPayload((prev) => {
            const list = prev!.configTableRows.hypothesis_ai_config ?? [];
            const idx = list.findIndex((r) => r.config_key === configKey);
            const next =
                idx >= 0
                    ? list.map((r, i) => (i === idx ? { ...r, ...patch } : r))
                    : [
                          ...list,
                          {
                              config_key: configKey,
                              system_prompt: '',
                              user_prompt_template: '',
                              is_active: 1,
                              ...patch,
                          },
                      ];
            return {
                ...prev!,
                configTableRows: { ...prev!.configTableRows, hypothesis_ai_config: next },
            };
        });
    };

    const findRow = (configKey: string): HypothesisAIConfigRow | undefined =>
        rows.find((r) => r.config_key === configKey);

    const enabled = (settings.HYPOTHESIS_ENABLED ?? 'false') === 'true';

    const primaryValue: ProviderModelTempValue = {
        provider:
            (settings.HYPOTHESIS_AI_PRIMARY_PROVIDER as ProviderId) ||
            guessProvider(settings.HYPOTHESIS_AI_PRIMARY_MODEL),
        model: settings.HYPOTHESIS_AI_PRIMARY_MODEL ?? '',
        temperature: Number(settings.HYPOTHESIS_AI_PRIMARY_TEMPERATURE ?? 0.2),
    };

    const fallbackValue: ProviderModelTempValue = {
        provider:
            (settings.HYPOTHESIS_AI_FALLBACK_PROVIDER as ProviderId) ||
            guessProvider(settings.HYPOTHESIS_AI_FALLBACK_MODEL),
        model: settings.HYPOTHESIS_AI_FALLBACK_MODEL ?? '',
        temperature: Number(settings.HYPOTHESIS_AI_FALLBACK_TEMPERATURE ?? 0.2),
    };

    const handlePrimaryChange = (next: ProviderModelTempValue) => {
        setManySettings({
            HYPOTHESIS_AI_PRIMARY_PROVIDER: next.provider,
            HYPOTHESIS_AI_PRIMARY_MODEL: next.model,
            HYPOTHESIS_AI_PRIMARY_TEMPERATURE: String(next.temperature),
        });
    };

    const handleFallbackChange = (next: ProviderModelTempValue) => {
        setManySettings({
            HYPOTHESIS_AI_FALLBACK_PROVIDER: next.provider,
            HYPOTHESIS_AI_FALLBACK_MODEL: next.model,
            HYPOTHESIS_AI_FALLBACK_TEMPERATURE: String(next.temperature),
        });
    };

    if (rows.length === 0) {
        return <Empty description="No hypothesis_ai_config rows — has the SYR-90 seed SQL been run?" />;
    }

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    Day-0 brand research and the inferred-constraint Hypothesis formed from it.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>

            <Card title="Status" size="small" style={{ marginBottom: 16 }}>
                <Space>
                    <Switch
                        checked={enabled}
                        onChange={(checked) =>
                            setSetting('HYPOTHESIS_ENABLED', checked ? 'true' : 'false')
                        }
                        checkedChildren="enabled"
                        unCheckedChildren="disabled"
                    />
                    <Text type="secondary">
                        <code>HYPOTHESIS_ENABLED</code> — gates signup enqueue, the automated
                        tease release and the resolution sweep. Admin-triggered runs below work
                        regardless (concierge mode); their emails only go out via the explicit
                        Send button.
                    </Text>
                </Space>
            </Card>

            <Card title="LLM provider — primary + fallback" size="small" style={{ marginBottom: 16 }}>
                <ProviderModelTempForm
                    label="Primary"
                    value={primaryValue}
                    onChange={handlePrimaryChange}
                    anthropicModels={anthropicModels}
                    openaiModels={openaiModels}
                    googleModels={googleModels}
                />
                <div style={{ marginTop: 12 }}>
                    <ProviderModelTempForm
                        label="Fallback"
                        value={fallbackValue}
                        onChange={handleFallbackChange}
                        anthropicModels={anthropicModels}
                        openaiModels={openaiModels}
                        googleModels={googleModels}
                    />
                </div>
            </Card>

            <Card style={{ marginBottom: 16 }}>
                <Tabs
                    tabPosition="left"
                    style={{ minHeight: 500 }}
                    items={HYPOTHESIS_KEYS.map((type) => {
                        const row = findRow(type.configKey);
                        return {
                            key: type.configKey,
                            label: (
                                <span>
                                    {row ? (
                                        <Badge dot status="success" offset={[-4, 0]}>
                                            <span>{type.label}</span>
                                        </Badge>
                                    ) : (
                                        <span>{type.label}</span>
                                    )}
                                </span>
                            ),
                            children: (
                                <RowEditor
                                    row={row}
                                    onChange={(patch) => upsertRow(type.configKey, patch)}
                                    topAlert={
                                        <Alert
                                            type="info"
                                            showIcon
                                            icon={<InfoCircleOutlined />}
                                            style={{ marginBottom: 16 }}
                                            message={type.label}
                                            description={type.description}
                                        />
                                    }
                                    placeholdersAlert={PLACEHOLDERS}
                                />
                            ),
                        };
                    })}
                />
            </Card>



            <Paragraph type="secondary" style={{ marginTop: 16 }}>
                Each key must return ONLY a JSON object (no prose, no code fences). Research is
                queued at signup and runs while the password sits unset. Everything it produces is
                held against the research subject and read only by hypothesis formation — Brand
                Knowledge is the brand's to write, and nothing here touches it.
            </Paragraph>
        </div>
    );
};

// ----- Research jobs table --------------------------------------------

interface RowEditorProps {
    row: HypothesisAIConfigRow | undefined;
    onChange: (patch: Partial<HypothesisAIConfigRow>) => void;
    topAlert: React.ReactNode;
    placeholdersAlert: React.ReactNode;
}

const RowEditor: React.FC<RowEditorProps> = ({
    row,
    onChange,
    topAlert,
    placeholdersAlert,
}) => {
    return (
        <div>
            {topAlert}

            {row && (
                <Space style={{ marginBottom: 12 }} wrap>
                    <Tag color={isActiveBool(row.is_active) ? 'green' : 'default'}>
                        {isActiveBool(row.is_active) ? 'active' : 'inactive'}
                    </Tag>
                    <Switch
                        checked={isActiveBool(row.is_active)}
                        onChange={(checked) => onChange({ is_active: checked ? 1 : 0 })}
                        checkedChildren="on"
                        unCheckedChildren="off"
                    />
                </Space>
            )}

            <div style={{ marginBottom: 16 }}>
                <PromptEditor
                    consumerSubsystem="hypothesis_ai"
                    label="System Prompt"
                    value={row?.system_prompt ?? ''}
                    onChange={(v) => onChange({ system_prompt: v })}
                    rows={8}
                />
            </div>
            <div style={{ marginBottom: 16 }}>
                <PromptEditor
                    consumerSubsystem="hypothesis_ai"
                    label="User Prompt Template"
                    value={row?.user_prompt_template ?? ''}
                    onChange={(v) => onChange({ user_prompt_template: v })}
                    rows={14}
                />
            </div>

            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                message="Available Placeholders"
                description={placeholdersAlert}
            />
        </div>
    );
};
